import {Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {Store} from '@ngrx/store';
import {AppState} from '@store/state/app.state';
import {EnvironmentService} from '@iw-shop/core//services/environment.service';
import {Observable} from 'rxjs';
import {
    ApiUsersDeleteRequest,
    ApiUsersDeleteResponse,
    ApiUsersGetRequest,
    ApiUsersGetResponse,
    ApiUsersSetRequest,
    ApiUsersSetResponse, ApiUsersUpdateRequest, ApiUsersUpdateResponse
} from '@api/users';
import {take} from 'rxjs/operators';
import {ShopAccessForCustomer} from '@iw-shop/user/interfaces/user';
import {
    ApiUserEmailVerificationLostPasswordRequest,
    ApiUserEmailVerificationLostPasswordResponse
} from '@api/user-email-verification';
import {getSelectedCustomer} from '@store/selectors/user.selector';
import {CustomerAddress} from '@iw-shop/customer/interfaces/customer';
import {MappedCustomer} from '@store/state/user.state';

@Injectable({
    providedIn: 'root'
})
export class UserService {

    constructor(private http: HttpClient, private store: Store<AppState>, private environmentService: EnvironmentService) {
    }

    setUser(data: ApiUsersSetRequest['params']['data']): Observable<ApiUsersSetResponse> {
        const body: ApiUsersSetRequest = {
            id: 'setUser#INVALID_PARAMS',
            command: 'users/set',
            params: {
                data
            }
        };
        return this.http.post<ApiUsersSetResponse>(this.environmentService.environment.urlApi, JSON.stringify(body));
    }

    sendEmailVerificationLostPassword(username: string): Observable<ApiUserEmailVerificationLostPasswordResponse> {
        const body: ApiUserEmailVerificationLostPasswordRequest = {
            id: 'sendEmailVerificationLostPassword#INVALID_PARAMS',
            command: 'userEmailVerification/lostPassword',
            params: {
                data: {
                    username
                }

            }
        };
        return this.http.post<ApiUserEmailVerificationLostPasswordResponse>(this.environmentService.environment.urlApi, JSON.stringify(body));
    }

    getUsers(customerId: string): Observable<ApiUsersGetResponse> {
        const body: ApiUsersGetRequest = {
            id: 'getUser',
            command: 'users/get',
            params: {
                filters: [
                    {
                        fields: ['shopAccessForCustomers.customerId'],
                        values: [customerId]
                    }
                ],
                users: {
                    sort: [{
                        field: 'username',
                        direction: 1
                    }]
                }
            }
        };
        return this.http.post<ApiUsersGetResponse>(this.environmentService.environment.urlApi, JSON.stringify(body));
    }

    getSelectedCustomer(): MappedCustomer | undefined {
        let customer: MappedCustomer | undefined = undefined;
        this.store.select(getSelectedCustomer)
            .pipe(
                take(1)
            )
            .subscribe(
                r => {
                    if (r) {
                        customer = r;
                    }
                }
            );
        return customer;
    }

    getSelectedCustomerAddresses(params?: {
        type?: 'billing' | 'delivery',
        checkUserAccess?: boolean
    }): CustomerAddress[] {
        let addresses: CustomerAddress[] = [];
        this.store.select(getSelectedCustomer)
            .pipe(
                take(1)
            )
            .subscribe(
                r => {
                    if (r && r.addresses) {
                        r.addresses.map(
                            address => {
                                if (params?.checkUserAccess &&
                                    (
                                        params?.type == address.type ||
                                        !params.type ||
                                        !address.type)
                                ) {
                                    if (this.hasUserAccessForCustomerAddress({
                                        customerId: r?.id || '',
                                        addressId: address.addressId || ''
                                    })) {
                                        addresses.push(address);
                                    }
                                } else {
                                    addresses.push(address);
                                }

                            }
                        )
                    }
                }
            );

        return addresses;
    }

    hasUserAccessForCustomerAddress(params: { customerId: string, addressId: string }): boolean {
        let access: boolean = false;
        this.store.select(state => state.user)
            .pipe(
                take(1)
            )
            .subscribe(
                r => {
                    r.shopAccessForCustomers?.map(
                        customerAccess => {
                            if (
                                customerAccess.customerId == params.customerId &&
                                (
                                    !customerAccess.permissions?.addressIds ||
                                    (customerAccess.permissions?.addressIds?.length ?? 0) == 0 ||
                                    customerAccess.permissions.addressIds.includes(params.addressId)
                                )
                            ) {
                                access = true;
                            }
                        }
                    )
                }
            )
        return access;
    }

    hasLoggedInUserAccess(params: {
        type: 'invoices' | 'users' | 'favorites' | 'prices' | 'customerAddresses',
        customerId: string
    }): boolean {
        let access: boolean = true;
        this.store.select(state => state.user)
            .pipe(
                take(1)
            )
            .subscribe(
                r => {
                    r.shopAccessForCustomers?.map(
                        customerAccess => {
                            if (customerAccess.customerId == params.customerId) {
                                access = false;

                                if (customerAccess.permissions?.grant.includes(params.type)) {
                                    access = true;
                                }
                            }
                        }
                    )
                }
            )
        return access;
    }

    getUserShopAccessForCustomers(): ShopAccessForCustomer[] {
        let shopAccessForCustomers: ShopAccessForCustomer[] = [];
        this.store.select(state => state.user)
            .pipe(
                take(1)
            )
            .subscribe(
                r => {
                    if (r.shopAccessForCustomers) {
                        shopAccessForCustomers = [...r.shopAccessForCustomers];
                    }
                }
            )
        return shopAccessForCustomers;
    }

    getUser(userId: string, customerId?: string): Observable<ApiUsersGetResponse> {
        const body: ApiUsersGetRequest = {
            id: 'getUser',
            command: 'users/get',
            params: {
                filters: [
                    {
                        fields: ['id'],
                        values: [userId]
                    }
                ],
                users: {}
            }
        };
        if (customerId) {
            body.params.filters?.push({
                fields: ['shopAccessForCustomers.customerId'],
                values: [customerId]
            })
        }
        return this.http.post<ApiUsersGetResponse>(this.environmentService.environment.urlApi, JSON.stringify(body));
    }

    updatePassword(userId: string, password: string) {
        const body: ApiUsersUpdateRequest = {
            id: 'updateUser#INVALID_PARAMS',
            command: 'users/update',
            params: {
                data: {
                    id: userId,
                    password
                }
            }
        };
        return this.http.post<ApiUsersUpdateResponse>(this.environmentService.environment.urlApi, JSON.stringify(body));
    }

    update(data: ApiUsersUpdateRequest['params']['data']): Observable<ApiUsersUpdateResponse> {
        const body: ApiUsersUpdateRequest = {
            id: 'updateUser#INVALID_PARAMS',
            command: 'users/update',
            params: {
                data
            }
        };
        return this.http.post<ApiUsersUpdateResponse>(this.environmentService.environment.urlApi, JSON.stringify(body));
    }

    deleteUser(username: string): Observable<ApiUsersDeleteResponse> {
        const body: ApiUsersDeleteRequest = {
            id: 'deleteUser',
            command: 'users/delete',
            params: {
                data: {
                    username
                }
            }
        };
        return this.http.post<ApiUsersDeleteResponse>(this.environmentService.environment.urlApi, JSON.stringify(body));
    }

    getUserEmail(): string | null {
        let email = null;
        this.store.select(state => state.user)
            .pipe(
                take(1)
            )
            .subscribe(
                r => {
                    if (r.email) {
                        email = r.email;
                    }
                }
            )
        return email;
    }
}
